import { Component, computed, effect, EffectRef, ElementRef, inject, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { ButtonComponent } from '../../../../shared/button/button.component';
import { ButtonType } from '../../../../../models/enums/components/Button/ButtonType.enum';
import { AppViews } from '../../../../../models/enums/dvm/DvmViews.enum';
import { DvmService } from '../../../../../services/dvm.service';
import { SearchService } from '../../../../../services/components/search.service';
import { NavigationService } from '@services/navigation.service';
import { NavigationItem } from '@models/types/app/NavigationItem.type';
import { AmenitiesService } from '@services/amenities.service';
import { FilterService } from '@services/filter.service';
import { GeneralAdmission } from '@models/types/components/GeneralAdmission.type';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from '@services/ui.service';
import anime from 'animejs';
import { ViewerRouteParamsService } from '@services/viewer-route-params.service';
import { isInteriorViewId } from '@helpers/utils/dvm-node.util';

@Component({
    selector: 'app-back-button',
    imports: [ButtonComponent],
    templateUrl: './back-button.component.html',
    styleUrl: './back-button.component.css'
})
export class BackButtonComponent {

  @ViewChild('backButton', { read: ElementRef })
  private backButton!: ElementRef<ButtonComponent>;

  // SERVICES
  private   readonly routeParams:       ViewerRouteParamsService = inject(ViewerRouteParamsService);
  private   readonly dvmService:        DvmService               = inject(DvmService)
  private   readonly navigation:        NavigationService        = inject(NavigationService);
  private   readonly amsService:        AmenitiesService         = inject(AmenitiesService);
  private   readonly filterService:     FilterService            = inject(FilterService);
  private   readonly SearchService: SearchService            = inject(SearchService);



  // GLOBAL STATE
  protected readonly history:           Signal<NavigationItem[]> = this.navigation.history;
  protected readonly view:              Signal<AppViews | null>  = this.dvmService.view;
  protected readonly hasViewId:         boolean                  = this.routeParams.hasViewId;


  // STATE
  protected readonly showElement:       WritableSignal<boolean>  = signal(false);
  protected          animationDuration: number                   = inject(SearchService).duration;
  protected readonly showBackButton:    Signal<boolean>          = computed((): boolean => ( (this.view() === AppViews.Pano && this.history().length > 0) || this.history().length > 1) && !this.hasViewId );

  //CONFIG
  private readonly uiconfig:            Signal<UiConfiguration>  = inject(UiService).uiConfig;
  private readonly gaExceptions:        Signal<GeneralAdmission> = computed((): GeneralAdmission=> this.uiconfig().GeneralAdmission);
    
  
  // BUTTON 
  protected readonly buttonType:        ButtonType               = ButtonType.Pill;


  // EFFECTS
  public backButtonVisibility:          EffectRef                = effect((): void => {
    
    const show: boolean = this.showBackButton();

    if( this.showElement() === show ) return
      
    this.toggleBackButton(show);

  })


  // METHODS
  public back(): Promise<void> | void {

    if (this.view() !== AppViews.Pano) return this.handleBack();

    const isGaException: boolean = this.gaExceptions().enabled && this.gaExceptions().data.includes(this.history()[1]?.map_id),
          isInterior:    boolean = isInteriorViewId(this.history()[1]?.map_id);

    if (isGaException || isInterior) {

      return this.navigation.back().then((): void => this.dvmService.close3dView());

    }
    else if(this.history().length === 1) {

      const topviewMapId: string = this.navigation.history()[0].map_id;

      this.dvmService.loadMap(topviewMapId).subscribe({error: (error: unknown): void => console.error('Error loading previous map', error)});

      return this.dvmService.close3dView();

    }
    else {

      return this.dvmService.close3dView();

    }

  }

  private handleBack(): void {
    if(this.SearchService.searchValue()) {
      this.SearchService.setSearchValue(null);
    }

    if(this.amsService.hasSelectedAmenities() && !this.filterService.hasFilters()) {
      this.amsService.resetSelectedAmenities();
    }

    this.navigation.back().then(previousItem => previousItem ? 
      this.dvmService.loadMap(previousItem.map_id).subscribe({error: (error: unknown): void => console.error('Error loading previous map', error)}) :
      null
    )

  }
  
  private toggleBackButton(show: boolean): void {

    if(show && !this.showElement()) this.showElement.set(true);
    
    anime({
      targets:  this.backButton.nativeElement,
      opacity:  show ? 1 : 0,
      duration: this.animationDuration,
      easing:   'easeInOutQuad',
      complete: () => {
        if(!show) this.showElement.set(false);
      },
    })

  }

}
